import React, { useState, useRef } from 'react';
import { API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UploadSimple, CheckCircle, Warning, Question, File, Spinner } from '@phosphor-icons/react';

export default function BatchUpload3DDialog({ open, onOpenChange, onSuccess }) {
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(files);
    setResults(null);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      selectedFiles.forEach(f => fd.append('files', f));
      const res = await axios.post(`${API}/validation3d/batch-upload`, fd);
      setResults(res.data);
      if (res.data.assigned > 0) {
        toast.success(`${res.data.assigned} fichier(s) assigne(s) automatiquement`);
        onSuccess?.();
      }
      if (res.data.unrecognized > 0) {
        toast.warning(`${res.data.unrecognized} fichier(s) non reconnu(s)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'import par lot');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = (v) => {
    if (!v) {
      setResults(null);
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-white max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="batch-upload-3d-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UploadSimple size={24} className="text-violet-600" />
            Import STL de validation 3D par lot
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-zinc-50 border border-zinc-200 p-4 rounded-sm">
            <p className="text-sm text-zinc-700 mb-2">
              Selectionnez plusieurs fichiers STL. Le systeme reconnaitra automatiquement 
              les numeros de serie dans les noms de fichiers et assignera chaque STL a la commande correspondante.
            </p>
            <p className="text-xs text-zinc-500">
              Format recommande : le nom du fichier doit contenir le numero de serie (ex: <code className="bg-zinc-200 px-1">26ABC1234L_validation.stl</code>)
            </p>
          </div>

          {/* File selection */}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".stl"
              multiple
              className="hidden"
              onChange={handleFilesSelected}
              data-testid="batch-file-input"
            />
            <Button
              type="button"
              variant="outline"
              className="w-full rounded-sm h-20 border-dashed border-2 hover:bg-zinc-50"
              onClick={() => fileInputRef.current?.click()}
              data-testid="batch-select-files-btn"
            >
              <div className="flex flex-col items-center gap-1">
                <UploadSimple size={24} className="text-zinc-400" />
                <span className="text-sm text-zinc-600">
                  {selectedFiles.length > 0
                    ? `${selectedFiles.length} fichier(s) selectionne(s)`
                    : 'Cliquer pour selectionner des fichiers STL'}
                </span>
              </div>
            </Button>
          </div>

          {/* Selected files list */}
          {selectedFiles.length > 0 && !results && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Fichiers selectionnes</p>
              {selectedFiles.map((f, i) => (
                <div key={`${f.name}-${f.size}`} className="flex items-center gap-2 text-sm bg-zinc-50 p-2 rounded-sm border border-zinc-200">
                  <File size={14} className="text-zinc-400" />
                  <span className="truncate">{f.name}</span>
                  <span className="text-xs text-zinc-400 ml-auto">{(f.size / 1024).toFixed(0)} Ko</span>
                </div>
              ))}
            </div>
          )}

          {/* Results */}
          {results && (
            <div className="space-y-3" data-testid="batch-results">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-sm text-center">
                  <p className="text-2xl font-bold text-emerald-700">{results.assigned}</p>
                  <p className="text-xs text-emerald-600">Assigne(s)</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 p-3 rounded-sm text-center">
                  <p className="text-2xl font-bold text-amber-700">{results.unrecognized}</p>
                  <p className="text-xs text-amber-600">Non reconnu(s)</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 p-3 rounded-sm text-center">
                  <p className="text-2xl font-bold text-blue-700">{results.manual_review}</p>
                  <p className="text-xs text-blue-600">A verifier</p>
                </div>
              </div>

              {/* Visualization info */}
              {results.visualization_activated > 0 && (
                <div className="bg-violet-50 border border-violet-200 p-3 rounded-sm text-sm text-violet-700">
                  <CheckCircle size={16} className="inline mr-1" weight="fill" />
                  {results.visualization_activated} visualisation(s) activee(s) — emails envoyes aux clients
                </div>
              )}

              {/* Assigned details */}
              {results.details?.assigned?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-emerald-600 uppercase tracking-wider mb-1">Fichiers assignes</p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {results.details.assigned.map((a, i) => (
                      <div key={a.file_id || a.filename} className="flex items-center gap-2 text-xs bg-emerald-50 p-2 rounded-sm border border-emerald-200" data-testid={`batch-assigned-${i}`}>
                        <CheckCircle size={14} className="text-emerald-600 flex-shrink-0" weight="fill" />
                        <span className="truncate font-medium">{a.filename}</span>
                        <span className="text-emerald-500">NS: {a.serial}</span>
                        <span className="text-emerald-500">{a.side === 'left' ? 'OG' : 'OD'}</span>
                        {a.visualization_activated && <span className="bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded text-[10px]">Email envoye</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Unrecognized details */}
              {results.details?.unrecognized?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-amber-600 uppercase tracking-wider mb-1">Fichiers non reconnus</p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {results.details.unrecognized.map((u, i) => (
                      <div key={u.filename} className="flex items-center gap-2 text-xs bg-amber-50 p-2 rounded-sm border border-amber-200" data-testid={`batch-unrecognized-${i}`}>
                        <Warning size={14} className="text-amber-600 flex-shrink-0" />
                        <span className="truncate font-medium">{u.filename}</span>
                        <span className="text-amber-500 ml-auto">{u.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Manual review details */}
              {results.details?.manual_review?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-blue-600 uppercase tracking-wider mb-1">A verifier manuellement</p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {results.details.manual_review.map((m, i) => (
                      <div key={`manual-${m.filename}`} className="text-xs bg-blue-50 p-2 rounded-sm border border-blue-200" data-testid={`batch-manual-${i}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Question size={14} className="text-blue-600 flex-shrink-0" />
                          <span className="truncate font-medium">{m.filename}</span>
                        </div>
                        <p className="text-blue-500 ml-5">
                          Correspondances possibles: {m.possible_matches?.map(pm => `${pm.serial} (${pm.side === 'left' ? 'OG' : 'OD'})`).join(', ')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => handleClose(false)} className="rounded-sm">
            {results ? 'Fermer' : 'Annuler'}
          </Button>
          {!results && (
            <Button
              onClick={handleUpload}
              disabled={selectedFiles.length === 0 || uploading}
              className="rounded-sm bg-violet-600 hover:bg-violet-700"
              data-testid="batch-upload-btn"
            >
              {uploading ? (
                <><span className="animate-spin mr-2">&#8987;</span>Import en cours...</>
              ) : (
                <><UploadSimple size={16} className="mr-2" />Importer {selectedFiles.length} fichier(s)</>
              )}
            </Button>
          )}
          {results && (
            <Button
              onClick={() => { setResults(null); setSelectedFiles([]); if (fileInputRef.current) fileInputRef.current.value = ''; }}
              className="rounded-sm bg-zinc-900 hover:bg-zinc-800"
              data-testid="batch-new-upload-btn"
            >
              Nouvel import
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
